import { isShopifyEmbedded } from "@shopify/app-bridge-utils"
import { ActionListItemDescriptor, ToastProps } from "@shopify/polaris"
import { action, computed, makeObservable, observable } from "mobx"

import { Country, timeZone } from "@/api/base"
import commonApi from "@/api/common"
import { CommonImageItem } from "@/api/common/types/CommonImages"
import { RecommendAppsHelloResponse } from "@/api/common/types/RecommendApps"
import { ThemeLayoutFilesListItem } from "@/api/shopify-graphql/shopify-files"
import { PublicModalId } from "@/component/PublicModal"
import { SupportLanguages } from "@/i18n/SupportLanguages"
import {
  default as ICommonStore,
  IContextualSaveBar,
  IPublicModal,
  IToastProps,
  IUserGuide,
  ValueOf,
} from "@/models/ICommonStore"
import stores from "@/stores/index"
import { LanguageUtils } from "@/utils/languages"

export default class CommonStore implements ICommonStore {
  @observable public showFeedbackModal: boolean = false
  @observable public contextualSaveBar: IContextualSaveBar = {
    disabled: false,
    loading: false,
    show: false,
    unlocking: false,
  }

  // 国家地区，暂时EDD有使用倒
  @observable public countries: Country[] = []
  @observable public timeZones: timeZone[] = []

  @observable public toastItems: { [id: string]: ToastProps } = {}
  @observable authToken: string | null = null
  @observable public languageItems: ActionListItemDescriptor[] = []
  @observable public languageActiveCode: string = "en"

  // 语言简码映射，将当前系统的语言代码映射到标准格式
  private readonly languageCodeMap: Record<string, string> = {
    "en": "en",
    "zh": "zh-CN",
    "zh-HK": "zh-TW",
    "fr": "fr",
    "de": "de",
    "it": "it",
    "es": "es",
    "pt": "pt-PT",
    "nl": "nl",
    "sv": "sv",
    "da": "da",
    "cs": "cs",
    "no": "no",
    "nb": "nb",
    "pl": "pl",
    "fi": "fi",
    "tr": "tr",
    "th": "th",
    "ja": "ja",
    "ko": "ko",
    "pt-BR": "pt-BR",
  }

  // 支持的语言简码列表
  public readonly supportedLangs: string[] = [
    "en", "zh-CN", "zh-TW", "fr", "de", "it", "es", "pt-PT",
    "nl", "sv", "da", "cs", "no", "nb", "pl", "fi", "tr", "th", "ja", "ko", "pt-BR",
  ]

  @observable public isCannyLoaded = false
  @observable public userGuide: IUserGuide = {
    dropshipper: undefined,
    emailAccentColor: "#0066cc",
    emailLang: "en",
    isGetEmailSet: false,
    langList: [{ label: "Dutch", value: "nl" }, { label: "English", value: "en" }, {
      label: "French",
      value: "fr",
    }, { label: "German", value: "de" }, { label: "Italian", value: "it" }, { label: "Spanish", value: "es" }, {
      label: "Danish",
      value: "da",
    }, { label: "Portuguese", value: "pt" }],
    openModal: false,
    header: {
      logo_url: "",
    },
    colors: {
      body_btn_bg: "#0066CC",
    },
  }
  @observable public publicModal: IPublicModal = {
    id: PublicModalId.DEFAULT,
    open: false,
  }
  @observable public ordersBannerShow: boolean = true

  // change Plan页面的跳转来源
  @observable public accountSource: string = "/billing"

  @observable banner20493: {visible: boolean}|null = null
  @observable recommendAppsPartnersData: RecommendAppsHelloResponse["recommend_apps_partners"] = []

  @observable public themeLayoutCommonImages: ThemeLayoutFilesListItem[] = []
  @observable public themeLayoutCommonImagesLoading = false
  @observable public themeLayoutCommonImagesLoadingMore = false
  @observable public themeLayoutCommonImagesHasMore = false

  private themeLayoutCommonImagesFetchSeq = 0
  private themeLayoutCommonImagesCursor: string | null = null
  /** 当前选中的图片 gid，分页时随每页请求一起回传，确保选中项位置稳定 */
  private themeLayoutCommonImagesGid = ""
  private static readonly THEME_IMAGES_PAGE_SIZE = 50

  constructor() {
    makeObservable(this)
    this.languageItems = this.getLanguageItems()
  }

  @computed
  get selectLanguage() {
    return this.languageItems.find(item => item.active)?.content ?? "English"
  }

  /**
   * 获取当前语言的标准简码
   * @returns 标准格式的语言简码
   */
  @computed
  get currentLanguageCode(): string {
    return this.languageCodeMap[this.languageActiveCode] || this.languageActiveCode
  }

  /**
   * 根据系统语言代码获取标准简码
   * @param systemCode 系统内部使用的语言代码
   * @returns 标准格式的语言简码
   */
  public getStandardLanguageCode(systemCode: string): string {
    return this.languageCodeMap[systemCode] || systemCode
  }

  /**
   * 获取所有支持的标准语言简码
   * @returns 标准语言简码数组
   */
  public getSupportedLanguageCodes(): string[] {
    return [...this.supportedLangs]
  }

  /** 设置当前语言 */
  @action.bound
  public setActiveLanguage(languageCode: SupportLanguages, isLocale?: boolean) {
    const languageChanged = this.languageActiveCode !== languageCode

    this.languageActiveCode = languageCode

    if (!window.isFromAdmin && isShopifyEmbedded()){
      window.cio && window.cio.changeLanguage(languageCode)

      window.cwreport && window.cwreport.setPublicProperty({
        language: languageCode,
      })
    }

    LanguageUtils.setLocale(languageCode, undefined, isLocale)

    // 套餐文案走后端多语言，仅在语言实际变化时刷新，避免与 UpgradeRequired 并发重复请求
    if (languageChanged) {
      stores.upgradeStore.updatePlans()
    }
  }

  @action.bound
  public setOrdersBannerShow(show: boolean) {
    this.ordersBannerShow = show
  }

  @action.bound
  public setAccountSource(path: string) {
    this.accountSource = path
  }

  @action.bound
  public setCountries(countries: Country[]) {
    this.countries = countries
  }

  @action.bound
  public setTimeZones(timeZones: timeZone[]) {
    this.timeZones = timeZones
  }

  @action.bound
  public addToast(toastProps: IToastProps): void {
    // const id = uniqueId("toast")
    const id = "toast"

    this.toastItems[id] = {
      ...toastProps,
      onDismiss: () => this.removeToast(id),
    }
  }

  @action.bound
  public removeToast(id: string): void {
    delete this.toastItems[id]
  }

  @action.bound
  public setIsCannyLoaded(value: boolean) {
    this.isCannyLoaded = value
  }

  @action.bound
  public setShowFeedbackModal(status: boolean): void {
    this.showFeedbackModal = status
  }

  @action.bound
  public setContextualSaveBar<K extends keyof IContextualSaveBar>(key: K, value: IContextualSaveBar[K]) {
    this.contextualSaveBar[key] !== value && (this.contextualSaveBar[key] = value)
  }

  @action.bound
  setAuthToken(token: string): void {
    this.authToken = token
  }

  @action.bound
  setUserGuideOption<T extends IUserGuide>(key: keyof T, val: ValueOf<T>): void {
    // @ts-ignore
    this.userGuide[key] = val
  }

  @action.bound
  public setActiveLanguageItem(local: SupportLanguages): void {
    this.languageItems.forEach(item => {
      item.active = local === item.id
    })
  }

  @action.bound
  openPublicModal(id: PublicModalId): void {
    this.publicModal.id = id
    this.publicModal.open = true
  }

  @action.bound
  closePublicModal(): void {
    this.publicModal.open = false
  }

  private getLanguageItems() {
    const langDisplayNameMap: { [lang in SupportLanguages]?: string } = {
      [SupportLanguages.Chinese]: "Chinese (Simplified)",
      [SupportLanguages.Chinese_Traditional]: "Chinese (Traditional)",
      [SupportLanguages.Portuguese_Portugal]: "Portuguese (Portugal)",
      [SupportLanguages.Portuguese_Brazil]: "Portuguese (Brazil)",
      [SupportLanguages.Norwegian_Bokmal]: "Norwegian (Bokmal)",
    }

    return Object.entries(SupportLanguages)
      .sort()// 从 A-Z 排序
      .map(([language, local]): ActionListItemDescriptor => {
        return {
          active: local === "en",
          content: langDisplayNameMap[local] || language.replace("_", " "),
          id: local, // 默认选择英语
          onAction() {
            LanguageUtils.setLocale(local)
          },
        }
      })
  }


  @action.bound
  public async getBanner20493(): Promise<{ visible: boolean }> {
    if (this.banner20493 !== null) {
      return this.banner20493
    }

    const visible = await commonApi.getBanner(20493)
      .then(resp => resp.data.banner.visible)

    this.banner20493 = {
      visible,
    }

    return this.banner20493
  }

  @action.bound
  public closeBanner20493() {
    if (this.banner20493) {
      this.banner20493.visible = false
    }

    commonApi.updateBanner(20493, false)
  }
  @action.bound
  public setRecommendAppsPartnersData(data: RecommendAppsHelloResponse["recommend_apps_partners"]) {
    this.recommendAppsPartnersData = data
  }

  private static readonly SHOPIFY_MEDIA_IMAGE_GID_PREFIX = "gid://shopify/MediaImage/"

  private mapCommonImageToGalleryItem(row: CommonImageItem): ThemeLayoutFilesListItem {
    const name = row.source_filename?.trim()

    return {
      id: row.gid,
      label: name || row.source || "Image",
      thumbnailUrl: row.url,
      deletable: row.gid.startsWith(CommonStore.SHOPIFY_MEDIA_IMAGE_GID_PREFIX),
    }
  }

  private async fetchThemeImagesPage(
    cursor: string | null,
  ): Promise<{ items: ThemeLayoutFilesListItem[]; nextCursor: string | null; hasMore: boolean }> {
    const res = await commonApi.getCommonImages({
      gid: this.themeLayoutCommonImagesGid,
      limit: CommonStore.THEME_IMAGES_PAGE_SIZE,
      ...(cursor ? { cursor } : {}),
    })
    const block = res.data.images
    const items = (block?.items ?? []).map((row) => this.mapCommonImageToGalleryItem(row))

    return {
      items,
      nextCursor: block?.pagination?.next_cursor ?? null,
      hasMore: Boolean(block?.pagination?.has_more),
    }
  }

  @action.bound
  public async fetchThemeLayoutCommonImages(opts?: { gid?: string }): Promise<void> {
    // 每次打开都重新加载，不使用缓存；重置游标，bump seq 让进行中的分页追加失效
    const seq = ++this.themeLayoutCommonImagesFetchSeq

    this.themeLayoutCommonImagesGid = opts?.gid ?? ""
    this.themeLayoutCommonImagesCursor = null
    this.themeLayoutCommonImagesLoading = true
    // 重置 loadingMore：若有进行中的分页追加，它因 seq 改变会被丢弃且无法自行复位
    this.themeLayoutCommonImagesLoadingMore = false

    try {
      const page = await this.fetchThemeImagesPage(null)

      if (seq !== this.themeLayoutCommonImagesFetchSeq) {
        return
      }

      this.themeLayoutCommonImages = page.items
      this.themeLayoutCommonImagesCursor = page.nextCursor
      this.themeLayoutCommonImagesHasMore = page.hasMore
    } finally {
      if (seq === this.themeLayoutCommonImagesFetchSeq) {
        this.themeLayoutCommonImagesLoading = false
      }
    }
  }

  /** 滚动到底部时追加下一页；并发、无更多、无游标时均为空操作 */
  @action.bound
  public async loadMoreThemeLayoutCommonImages(): Promise<void> {
    if (
      this.themeLayoutCommonImagesLoadingMore
      || this.themeLayoutCommonImagesLoading
      || !this.themeLayoutCommonImagesHasMore
      || !this.themeLayoutCommonImagesCursor
    ) {
      return
    }

    const seq = this.themeLayoutCommonImagesFetchSeq
    const cursor = this.themeLayoutCommonImagesCursor

    this.themeLayoutCommonImagesLoadingMore = true

    try {
      const page = await this.fetchThemeImagesPage(cursor)

      // 加载期间若发生了重新加载（seq 改变），丢弃这批结果
      if (seq !== this.themeLayoutCommonImagesFetchSeq) {
        return
      }

      // 去重追加，避免游标边界 / 裁剪导致的重复项
      const existing = new Set(this.themeLayoutCommonImages.map((item) => item.id))
      const fresh = page.items.filter((item) => !existing.has(item.id))

      this.themeLayoutCommonImages = [...this.themeLayoutCommonImages, ...fresh]
      this.themeLayoutCommonImagesCursor = page.nextCursor
      this.themeLayoutCommonImagesHasMore = page.hasMore
    } finally {
      if (seq === this.themeLayoutCommonImagesFetchSeq) {
        this.themeLayoutCommonImagesLoadingMore = false
      }
    }
  }
}
